import React, { useState, useContext } from 'react';
import { styled } from '@mui/material/styles';
import { Grid, Paper, Modal, Box } from '@mui/material';
import { captureException as sentryCaptureException } from '@sentry/react';
import { deleteField, doc, writeBatch, getDoc } from 'firebase/firestore';

import { db } from '../../../firebase-config';
import { AuthContext } from '../../../auth-context';
import StatusCardPresentation from './status-card-presentation';
import PaymentContainer from '../../checkout/PaymentContainer';
import { bookingPriceTranslator } from '../../../utils/form-translators';
import { AWAITING_SUBSTITUTION_FEE, BOOKING_STATUS, CHECKUP } from '../../../utils/constants';
import { CHECKUP_STATUS } from '../../form/constants';
import { updateCheckupStatuses } from '../../form/helper';

const PREFIX = 'StatusCard';

const { NOT_STARTED } = CHECKUP_STATUS;

const classes = {
  section: `${PREFIX}-section`,
  formHeading: `${PREFIX}-formHeading`,
  paper: `${PREFIX}-paper`,
  paperHolder: `${PREFIX}-paperHolder`,
  cancelButtonHolder: `${PREFIX}-cancelButtonHolder`,
  cancelButton: `${PREFIX}-cancelButton`,
  button: `${PREFIX}-button`,
  gridItem: `${PREFIX}-gridItem`,
  divider: `${PREFIX}-divider`,
  link: `${PREFIX}-link`,
  textButton: `${PREFIX}-textButton`,
  capitalizedHeading: `${PREFIX}-capitalizedHeading`,
  modalGrid: `${PREFIX}-modalGrid`,
};

const StyledGrid = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  // height: '100%',
  width: '100%',
  marginBottom: 50,

  [`&.${classes.section}`]: {},

  [`& .${classes.formHeading}`]: {
    textTransform: 'capitalize',
  },

  [`& .${classes.paper}`]: {
    position: 'relative',
    padding: '20px',
    margin: '20px 20px 0px 20px',
    width: '100%',
  },

  [`& .${classes.paperHolder}`]: {
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
  },

  [`& .${classes.cancelButtonHolder}`]: {
    position: 'relative',
  },

  [`& .${classes.cancelButton}`]: {
    height: 50,
    position: 'absolute',
    right: 0,
    top: 0,
  },

  [`& .${classes.button}`]: {
    position: 'absolute',
    bottom: 20,
  },

  [`& .${classes.gridItem}`]: {
    height: 30,
  },

  [`& .${classes.divider}`]: {
    marginBottom: 10,
  },

  [`& .${classes.link}`]: {
    textDecoration: 'none',
  },

  [`& .${classes.textButton}`]: {
    padding: '10px 0px 0px 0px',
  },

  [`& .${classes.capitalizedHeading}`]: {
    textTransform: 'capitalize',
  },
}));

const StyledModalContainer = styled(Modal)(() => ({
  margin: '10px',
  [`& .${classes.modalGrid}`]: {
    margin: '10% auto',
    '@media (max-width: 900px)': {
      margin: '10px auto',
      maxWidth: '600px',
      overflow: 'auto',
    },
    maxWidth: '1200px',
    borderRadius: '5px',
    maxHeight: '90vh',
  },
}));

/**
 *
 * @param {object} props
 * @param {Banner} props.statusObj - The status object containing details about the status.
 * @param {Banner[]} [props.pendingConsults] - An array of pending consults.
 * @param {React.Dispatch<React.SetStateAction<Banner[]>>} [props.setPendingConsults] - Function to update the pending consults.
 * @param {React.Dispatch<React.SetStateAction<string[]>>} [props.setThumbnailsToDisplay] - Function to update the thumbnails to display.
 */
const StatusCard = ({ statusObj, pendingConsults, setPendingConsults, setThumbnailsToDisplay }) => {
  const { user } = useContext(AuthContext);

  const [consultPriceIds, setConsultPriceIds] = useState(/**  @type {string[]} */ ([]));
  const [formRef, setFormRef] = useState('');
  const [termsRead, setTermsRead] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const statusData =
    statusObj.title === BOOKING_STATUS.AWAITING_BOOKING_PAYMENT
      ? { ...statusObj, consultPriceIds: [bookingPriceTranslator[statusObj.formName]] }
      : statusObj;

  /**
   *
   * @param {object} props
   * @param {string[]} props.paymentRef - The payment reference IDs for the consult being paid.
   * @param {string} props.healthCategory - The health category of the consult being paid for.
   */
  const consultPaymentButton = ({ paymentRef, healthCategory }) => {
    setModalOpen(true);
    setConsultPriceIds(paymentRef);
    setFormRef(healthCategory);
  };

  const clearForm = async (formName) => {
    if (!pendingConsults?.length || !setPendingConsults || !setThumbnailsToDisplay) {
      return;
    }

    const newPendingTreatments = pendingConsults.filter((oldTreatment) => oldTreatment.formName !== formName);

    setPendingConsults(newPendingTreatments);
    setThumbnailsToDisplay((tl) => [...tl, formName]);
    try {
      if (!user?.uid) {
        return;
      }

      const batch = writeBatch(db);
      const patientRef = doc(db, 'patients', user.uid);
      const patientGeneralInfoRef = doc(db, 'patients', user.uid, 'general', 'information');

      if (formName === CHECKUP) {
        const patientDoc = await getDoc(patientRef);
        const patientData = /** @type {GeneralPatientInfo} */ (patientDoc.data());
        const checkups = patientData?.checkups || {};
        const checkupKeys = Object.keys(checkups);

        await updateCheckupStatuses({
          nowInMillis: Date.now(),
          patientId: user?.uid,
          healthCategories: checkupKeys,
          checkupStatus: NOT_STARTED,
          paymentReference: statusData?.reference,
        });
      }

      batch.update(patientRef, {
        [`forms.${formName}`]: deleteField(),
        banner: deleteField(),
      });
      batch.update(patientGeneralInfoRef, {
        [`forms.${formName}`]: deleteField(),
      });
      await batch.commit();
    } catch (error) {
      sentryCaptureException(error, {
        extra: { formName, newPendingTreatments, issueIn: 'handleRemoveForm' },
      });
    }
  };

  const payPriceDifference = () => {
    setModalOpen(true);
  };

  return (
    <StyledGrid data-testid="Patient-Notications-Page" className={classes.section}>
      <Paper className={classes.paper}>
        <StatusCardPresentation
          statusObj={statusData}
          pendingConsults={pendingConsults}
          consultPaymentButton={consultPaymentButton}
          clearForm={clearForm}
          payPriceDifference={payPriceDifference}
        />
      </Paper>

      <StyledModalContainer open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box className={classes.modalGrid}>
          {statusData.title === BOOKING_STATUS.AWAITING_BOOKING_PAYMENT && (
            <PaymentContainer
              mode="booking fee"
              type="booking fee"
              description="Rebooking Fee"
              selectedTreatments={consultPriceIds}
              formName={formRef}
            />
          )}
          {statusData.title === AWAITING_SUBSTITUTION_FEE && <PaymentContainer reference={statusData.reference} />}
          {statusData.title !== AWAITING_SUBSTITUTION_FEE &&
            statusData.title !== BOOKING_STATUS.AWAITING_BOOKING_PAYMENT && (
              <PaymentContainer
                termsRead={termsRead}
                setTermsRead={setTermsRead}
                mode="consult"
                type="consult"
                description="Consultation Fee"
                selectedTreatments={consultPriceIds}
                formName={formRef}
              />
            )}
        </Box>
      </StyledModalContainer>
    </StyledGrid>
  );
};

export default StatusCard;
